import { useState, useEffect, useCallback, useRef } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { downloadPngFromVariantImageResponse } from "@/lib/qrDownload";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { QrCode, Plus, Download, BarChart3, Pencil, Trash2, Eye, Copy, Crosshair, Palette, Upload, Type, Grid3X3, Circle, Square, RectangleHorizontal, Share2, Crop, UserPlus, Phone, Building2, Mail, Globe, MapPin, Package, Layers, Users, Briefcase, Minus, Settings2, CheckCircle2, ExternalLink, ArrowRight, WalletCards, Lock, CircleHelp } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { canCreateVariant, getMaxVariants, hasFeature } from "@/lib/entitlements";
import ImageCropEditor from "@/components/ImageCropEditor";
import QrExportModal from "@/components/QrExportModal";
import { useLanguage } from "@/contexts/LanguageContext";

const PRESETS = [
  { value: 'full', label: 'Full Profile', desc: 'Avatar, title, bio, cover, all links', icon: Layers, accent: 'from-indigo-500 to-violet-500' },
  { value: 'social', label: 'Social Only', desc: 'Avatar, title, links only', icon: Users, accent: 'from-cyan-500 to-blue-500' },
  { value: 'contact', label: 'Contact Only', desc: 'Avatar, title, bio — no links', icon: UserPlus, accent: 'from-emerald-500 to-teal-500' },
  { value: 'business', label: 'Business Card', desc: 'Everything — polished layout', icon: Briefcase, accent: 'from-orange-500 to-amber-500' },
  { value: 'minimal', label: 'Minimal', desc: 'Title and links only', icon: Minus, accent: 'from-zinc-500 to-slate-500' },
  { value: 'custom', label: 'Custom', desc: 'Choose what to show', icon: Settings2, accent: 'from-pink-500 to-rose-500' },
];

const SECTION_KEYS = [
  { key: 'show_avatar', label: 'Avatar' },
  { key: 'show_title', label: 'Title' },
  { key: 'show_description', label: 'Description / Bio' },
  { key: 'show_links', label: 'Links' },
  { key: 'show_cover', label: 'Cover Image' },
  { key: 'show_hero', label: 'Hero Media' },
];

const PRESET_SECTIONS = {
  full: { show_avatar: true, show_title: true, show_description: true, show_links: true, show_cover: true, show_hero: true, avatar_offset_x: 0, avatar_offset_y: 0 },
  social: { show_avatar: true, show_title: true, show_description: false, show_links: true, show_cover: false, show_hero: false, avatar_offset_x: 0, avatar_offset_y: 0 },
  contact: { show_avatar: true, show_title: true, show_description: true, show_links: false, show_cover: false, show_hero: false, avatar_offset_x: 0, avatar_offset_y: 0 },
  business: { show_avatar: true, show_title: true, show_description: true, show_links: true, show_cover: true, show_hero: true, avatar_offset_x: 0, avatar_offset_y: 0 },
  minimal: { show_avatar: false, show_title: true, show_description: false, show_links: true, show_cover: false, show_hero: false, avatar_offset_x: 0, avatar_offset_y: 0 },
  custom: { show_avatar: true, show_title: true, show_description: true, show_links: true, show_cover: true, show_hero: true, avatar_offset_x: 0, avatar_offset_y: 0 },
};

const ANIMATION_TYPES = [
  { value: 'none', label: 'No Animation', desc: 'Elements appear instantly' },
  { value: 'fade', label: 'Fade In', desc: 'Smooth opacity transition' },
  { value: 'slide-up', label: 'Slide Up', desc: 'Rise from below' },
  { value: 'slide-down', label: 'Slide Down', desc: 'Drop from above' },
  { value: 'slide-left', label: 'Slide Left', desc: 'Enter from left' },
  { value: 'slide-right', label: 'Slide Right', desc: 'Enter from right' },
  { value: 'zoom-in', label: 'Zoom In', desc: 'Scale up with bounce' },
  { value: 'bounce-in', label: 'Bounce In', desc: 'Playful bouncy entrance' },
  { value: 'rotate-in', label: 'Rotate In', desc: 'Spin into view' },
  { value: 'flip', label: 'Flip Y', desc: '3D flip horizontally' },
  { value: 'flip-x', label: 'Flip X', desc: '3D flip vertically' },
  { value: 'swing', label: 'Swing', desc: 'Pendulum swing effect' },
  { value: 'elastic', label: 'Elastic', desc: 'Stretchy bounce effect' },
  { value: 'stagger', label: 'Stagger', desc: 'Cascade with tilt' },
];

const DOT_STYLES = [
  { id: 'square', label: 'Square', icon: Square },
  { id: 'rounded-half', label: 'Rounded', icon: RectangleHorizontal },
  { id: 'rounded-full', label: 'Pill', icon: Circle },
  { id: 'circle', label: 'Circle', icon: Circle },
  { id: 'gapped', label: 'Gapped', icon: Grid3X3 },
];

const CORNER_STYLES = [
  { id: 'square', label: 'Sharp' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'dot', label: 'Dot' },
];

const DEFAULT_STYLE = {
  qr_preset: 'classic', engraving_safe: false, custom_colors: null, dot_style: null,
  corner_style: 'square', logo_url: null, frame_text: '', export_preferences: { default_format: 'png', default_size: 'standard' },
  logo_scale: null, logo_offset_x: null, logo_offset_y: null,
};

function QrStyleSwatch({ style, selected, onClick }) {
  return (
    <button onClick={onClick} data-testid={`qr-style-${style.id}`}
      className={`relative flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all text-center min-w-[80px] ${
        selected ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-primary/30 bg-card hover:bg-secondary/40'
      }`}>
      <div className="w-9 h-9 rounded-lg border border-border/50 overflow-hidden flex items-center justify-center" style={{ backgroundColor: style.colors.back }}>
        <div className="grid grid-cols-3 gap-[2px]">
          {[...Array(9)].map((_, i) => (
            <div key={i} className={style.module === 'circle' ? 'rounded-full' : style.module === 'rounded' ? 'rounded-[2px]' : ''}
              style={{ width: i === 4 ? 0 : 5, height: i === 4 ? 0 : 5, backgroundColor: i === 4 ? 'transparent' : style.colors.front }}
            />
          ))}
        </div>
      </div>
      <span className="text-[10px] font-medium leading-tight">{style.name}</span>
      {style.id === 'engraving' && <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[7px] font-bold px-1.5 py-0.5 rounded-full">LASER</span>}
    </button>
  );
}

function LivePreview({ styleData, loading, previewImage, onLogoOffsetChange, hasLogo }) {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = (e) => {
    if (!hasLogo || !onLogoOffsetChange) return;
    e.preventDefault();
    draggingRef.current = true;
    setDragging(true);
    containerRef.current?.setPointerCapture?.(e.pointerId);
    updateOffsetFromPointer(e);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    updateOffsetFromPointer(e);
  };

  const handlePointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    containerRef.current?.releasePointerCapture?.(e.pointerId);
  };

  const updateOffsetFromPointer = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;
    // Map from [0,1] to [-0.35, 0.35] offset (center = 0.5 → 0)
    const ox = Math.max(-0.35, Math.min(0.35, (relX - 0.5) * 0.7));
    const oy = Math.max(-0.35, Math.min(0.35, (relY - 0.5) * 0.7));
    onLogoOffsetChange(ox, oy);
  };

  // Logo indicator position (fraction to CSS %)
  const indicatorX = 50 + ((styleData.logo_offset_x || 0) / 0.35) * 35;
  const indicatorY = 50 + ((styleData.logo_offset_y || 0) / 0.35) * 35;
  const logoScale = styleData.logo_scale || 0.18;
  const indicatorSize = Math.max(16, Math.min(48, logoScale * 200));

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        ref={containerRef}
        className={`relative w-72 h-72 bg-white rounded-2xl border-2 flex items-center justify-center overflow-hidden shadow-sm select-none ${
          hasLogo ? 'cursor-crosshair border-primary/30' : 'border-border/40'
        } ${dragging ? 'ring-2 ring-primary/40' : ''}`}
        onPointerDown={hasLogo ? handlePointerDown : undefined}
        onPointerMove={hasLogo ? handlePointerMove : undefined}
        onPointerUp={hasLogo ? handlePointerUp : undefined}
        data-testid="live-preview-container"
      >
        {loading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-20">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {previewImage ? (
          <img src={previewImage} alt="QR Preview" className="w-full h-full object-contain p-3 pointer-events-none" data-testid="live-preview-image" draggable={false} />
        ) : (
          <QrCode className="text-muted-foreground/20 pointer-events-none" size={64} />
        )}
        {/* Draggable logo position indicator */}
        {hasLogo && previewImage && (
          <div
            className="absolute z-10 pointer-events-none"
            style={{
              left: `${indicatorX}%`,
              top: `${indicatorY}%`,
              transform: 'translate(-50%, -50%)',
              transition: dragging ? 'none' : 'left 0.15s, top 0.15s',
            }}
            data-testid="logo-position-indicator"
          >
            <div
              className={`rounded-lg border-2 border-dashed ${dragging ? 'border-primary bg-primary/10' : 'border-primary/40 bg-primary/5'}`}
              style={{ width: `${indicatorSize}px`, height: `${indicatorSize}px` }}
            />
          </div>
        )}
      </div>
      <div className="text-center">
        <p className="text-[10px] text-muted-foreground font-medium tracking-wide uppercase">
          {hasLogo ? 'Drag to move logo' : 'Live Preview'}
        </p>
        {styleData.frame_text && (
          <p className="text-[11px] text-primary font-medium mt-1">
            Caption: "{styleData.frame_text}"
          </p>
        )}
      </div>
    </div>
  );
}

export default function QRCodePage() {
  const { authLoading, entitlements, entitlementsReady } = useAuth();
  const { t } = useLanguage();
  const ents = entitlements || {};
  const maxVariants = getMaxVariants(ents);
  const canCreative = hasFeature(ents, "creative_qr");
  const canSvg = hasFeature(ents, "export_svg");
  const canPdf = hasFeature(ents, "export_pdf");
  const canAnalytics = hasFeature(ents, "advanced_analytics");
  const canWallet = hasFeature(ents, "apple_wallet");
  const [variants, setVariants] = useState([]);
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editVariant, setEditVariant] = useState(null);
  const [analyticsVariant, setAnalyticsVariant] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [qrImages, setQrImages] = useState({});
  const [qrStyles, setQrStyles] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [logoCropOpen, setLogoCropOpen] = useState(false);
  const [logoCropSaving, setLogoCropSaving] = useState(false);
  const [exportVariant, setExportVariant] = useState(null);
  const [variantPreviews, setVariantPreviews] = useState({});
  const [packDownloading, setPackDownloading] = useState(false);
  const [walletDownloading, setWalletDownloading] = useState(null);
  const [successVariant, setSuccessVariant] = useState(null);
  const previewTimer = useRef(null);
  const previewReqId = useRef(0);
  const { theme } = useTheme();
  const canCreateMore = entitlementsReady && canCreateVariant(ents, variants.length);

  const [formData, setFormData] = useState({
    name: '', preset: 'full', animation: false, animation_type: 'none', audio_url: '',
    visible_sections: PRESET_SECTIONS.full, link_ids: [], ...DEFAULT_STYLE,
    contact_overrides: {},
  });

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, variantsRes, linksRes, stylesRes] = await Promise.all([
        api.get("/profile"), api.get("/variants"), api.get("/links"), api.get("/qr-styles"),
      ]);
      setProfile(profileRes.data);
      setVariants(variantsRes.data || []);
      setLinks(linksRes.data || []);
      setQrStyles(stylesRes.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load QR previews for variant cards
  useEffect(() => {
    variants.forEach(async (v) => {
      if (!variantPreviews[v.id]) {
        try {
          const res = await api.get(`/variants/${v.id}/image?format=png&size=8&base_url=${encodeURIComponent(BACKEND_URL)}`);
          setVariantPreviews((prev) => ({ ...prev, [v.id]: res.data?.data }));
        } catch {}
      }
    });
  }, [variants, variantPreviews]);

  // Keep old qrImages for the editor preview
  useEffect(() => {
    variants.forEach(async (v) => {
      if (!qrImages[v.id]) {
        try {
          const res = await api.get(`/variants/${v.id}/image?format=png&size=10&base_url=${encodeURIComponent(BACKEND_URL)}`);
          setQrImages((prev) => ({ ...prev, [v.id]: res.data }));
        } catch {}
      }
    });
  }, [variants, qrImages]);

  const handleDownloadPack = async () => {
    if (!canSvg) {
      toast.error("Upgrade required for bulk QR export");
      return;
    }
    setPackDownloading(true);
    try {
      const res = await api.get("/variants/export-pack", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smartqr-pack.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("QR pack downloaded!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download QR pack");
    } finally {
      setPackDownloading(false);
    }
  };

  const handleAddToWallet = async (variant) => {
    if (!canWallet) {
      toast.info(t("wallet.available"));
      window.location.href = '/dashboard/subscription';
      return;
    }
    setWalletDownloading(variant.id);
    try {
      const res = await api.get(`/variants/${variant.id}/apple-wallet`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.apple.pkpass' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${variant.name || 'smart-qr'}.pkpass`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(t("wallet.downloaded"));
    } catch (error) {
      toast.error(error?.response?.status === 503 ? t("wallet.setupMissing") : t("wallet.createFailed"));
    } finally {
      setWalletDownloading(null);
    }
  };

  // Live preview
  const fetchPreview = useCallback(async (sd) => {
    const reqId = ++previewReqId.current;
    setPreviewLoading(true);
    try {
      const payload = {
        qr_preset: sd.qr_preset || 'classic',
        engraving_safe: sd.engraving_safe || false,
        custom_colors: sd.custom_colors || null,
        dot_style: sd.dot_style || null,
        corner_style: sd.corner_style || 'square',
        logo_url: sd.logo_url || null,
        frame_text: sd.frame_text || null,
        logo_scale: sd.logo_scale || null,
        logo_offset_x: sd.logo_offset_x ?? null,
        logo_offset_y: sd.logo_offset_y ?? null,
      };
      const res = await api.post("/qr-preview", payload);
      if (reqId !== previewReqId.current) return;
      setPreviewImage(res.data?.data || null);
    } catch {
      if (reqId === previewReqId.current) setPreviewImage(null);
    } finally {
      if (reqId === previewReqId.current) setPreviewLoading(false);
    }
  }, []);

  const triggerPreview = useCallback((fd) => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      fetchPreview(fd);
    }, 250);
  }, [fetchPreview]);

  // Trigger preview on style-related field changes
  const updateForm = useCallback((updater) => {
    setFormData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      triggerPreview(next);
      return next;
    });
  }, [triggerPreview]);

  // Update form fields that should NOT trigger a QR preview refresh (text fields)
  const updateFormText = useCallback((updater) => {
    setFormData((prev) => {
      return typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
    });
  }, []);

  // Trigger initial preview when dialog opens
  useEffect(() => {
    if (createOpen || editVariant) {
      triggerPreview(formData);
    }
  }, [createOpen, editVariant]);

  const resetForm = () => {
    const defaults = {
      name: '', preset: 'full', animation: false, animation_type: 'none', audio_url: '',
      visible_sections: PRESET_SECTIONS.full, link_ids: [], ...DEFAULT_STYLE,
      contact_overrides: {},
    };
    setFormData(defaults);
    setPreviewImage(null);
  };

  const buildStyleObj = (fd) => ({
    qr_preset: fd.qr_preset, engraving_safe: fd.engraving_safe, custom_colors: fd.custom_colors,
    dot_style: fd.dot_style, corner_style: fd.corner_style, logo_url: fd.logo_url,
    logo_url_original: fd.logo_url_original, logo_crop: fd.logo_crop,
    frame_text: fd.frame_text, export_preferences: fd.export_preferences,
    logo_scale: fd.logo_scale, logo_offset_x: fd.logo_offset_x, logo_offset_y: fd.logo_offset_y,
  });

  const handleCreate = async () => {
    try {
      const res = await api.post("/variants", { ...formData, style: buildStyleObj(formData), contact_overrides: formData.contact_overrides || {} });
      toast.success("Variant created");
      setCreateOpen(false);
      resetForm();
      fetchData();
      // Show success state with the created variant data
      const createdVariant = res.data;
      if (createdVariant?.id) {
        setSuccessVariant(createdVariant);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create variant"); }
  };

  const handleUpdate = async () => {
    if (!editVariant) return;
    try {
      await api.put(`/variants/${editVariant.id}`, { ...formData, style: buildStyleObj(formData), contact_overrides: formData.contact_overrides || {} });
      toast.success("Variant updated");
      setEditVariant(null);
      // Clear both preview caches to force refresh
      setQrImages((prev) => { const n = { ...prev }; delete n[editVariant.id]; return n; });
      setVariantPreviews((prev) => { const n = { ...prev }; delete n[editVariant.id]; return n; });
      resetForm();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this QR variant?")) return;
    try { await api.delete(`/variants/${id}`); toast.success("Variant deleted"); fetchData(); } catch { toast.error("Failed to delete"); }
  };

  const openEdit = (v) => {
    const sd = v.style || {};
    const fd = {
      name: v.name, preset: v.preset, animation: v.animation,
      animation_type: v.animation_type || 'none', audio_url: v.audio_url || '',
      visible_sections: v.visible_sections || PRESET_SECTIONS[v.preset] || PRESET_SECTIONS.full,
      link_ids: v.link_ids || [],
      qr_preset: canCreative ? (sd.qr_preset || 'classic') : 'classic',
      engraving_safe: canCreative ? (sd.engraving_safe || false) : false,
      custom_colors: canCreative ? (sd.custom_colors || null) : null,
      dot_style: canCreative ? (sd.dot_style || null) : null,
      corner_style: canCreative ? (sd.corner_style || 'square') : 'square',
      logo_url: canCreative ? (sd.logo_url || null) : null,
      logo_url_original: canCreative ? (sd.logo_url_original || sd.logo_url || null) : null,
      logo_crop: canCreative ? (sd.logo_crop || null) : null,
      frame_text: canCreative ? (sd.frame_text || '') : '',
      export_preferences: sd.export_preferences || { default_format: 'png', default_size: 'standard' },
      contact_overrides: v.contact_overrides || {},
      logo_scale: canCreative ? (sd.logo_scale || null) : null,
      logo_offset_x: canCreative ? (sd.logo_offset_x ?? null) : null,
      logo_offset_y: canCreative ? (sd.logo_offset_y ?? null) : null,
    };
    if (!canCreative && fd.export_preferences?.default_format === 'svg') {
      fd.export_preferences = { ...fd.export_preferences, default_format: 'png' };
    }
    setFormData(fd);
    setEditVariant(v);
  };

  const openAnalytics = async (v) => {
    if (!canAnalytics) {
      toast.error("Upgrade required for advanced analytics");
      return;
    }
    setAnalyticsVariant(v);
    try { const res = await api.get(`/variants/${v.id}/analytics`); setAnalyticsData(res.data); } catch { setAnalyticsData(null); }
  };

  const downloadQR = async (variantId, format, highRes = false) => {
    if (format === "svg" && !canSvg) {
      toast.error("Upgrade required for SVG export");
      return;
    }
    if (format === "pdf" && !canPdf) {
      toast.error("Upgrade required for PDF export");
      return;
    }
    try {
      const size = highRes ? 40 : 10;
      const res = await api.get(`/variants/${variantId}/image?format=${format}&size=${size}&base_url=${encodeURIComponent(BACKEND_URL)}`);
      if (format === 'svg') {
        const blob = new Blob([res.data.data], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a');
        a.href = url; a.download = `qr-variant-${variantId}.svg`; a.rel = 'noopener';
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      } else {
        downloadPngFromVariantImageResponse(
          res.data,
          highRes ? `qr-variant-${variantId}-hires.png` : `qr-variant-${variantId}.png`,
        );
      }
      toast.success(`${highRes ? 'Hi-Res ' : ''}${format.toUpperCase()} downloaded`);
    } catch (e) {
      console.error("[QRCodePage] downloadQR failed", e);
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const downloadEngraving = async (variantId) => {
    try {
      const res = await api.get(`/variants/${variantId}/image?format=png&size=40&base_url=${encodeURIComponent(BACKEND_URL)}`);
      downloadPngFromVariantImageResponse(res.data, `qr-engraving-${variantId}.png`);
      toast.success("Engraving-safe export downloaded");
    } catch (e) {
      console.error("[QRCodePage] downloadEngraving failed", e);
      toast.error("Download failed");
    }
  };

  const copyLink = (variant) => { navigator.clipboard.writeText(`${BACKEND_URL}/api/q/${variant.token}`); toast.success("QR link copied"); };
  const copyShareLink = (variant) => {
    const shareUrl = `${window.location.origin}/v/${variant.token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied");
  };

  const handlePresetChange = (preset) => updateForm((p) => ({ ...p, preset, visible_sections: PRESET_SECTIONS[preset] || PRESET_SECTIONS.full }));
  const toggleSection = (key) => updateForm((p) => ({ ...p, preset: 'custom', visible_sections: { ...p.visible_sections, [key]: !p.visible_sections[key] } }));
  const toggleLinkId = (linkId) => updateForm((p) => {
    const ids = p.link_ids.includes(linkId) ? p.link_ids.filter((id) => id !== linkId) : [...p.link_ids, linkId];
    return { ...p, link_ids: ids };
  });

  const handleLogoUpload = async (e) => {
    if (!canCreative) {
      toast.error("Upgrade required for custom QR logo branding");
      e.target.value = "";
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const variantId = editVariant?.id || '';
      const res = await api.post(`/upload/qr_logo?variant_id=${variantId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateForm((p) => ({ ...p, logo_url: res.data.url, logo_url_original: res.data.url, logo_crop: null, logo_scale: 0.18, logo_offset_x: 0, logo_offset_y: 0 }));
      toast.success("Logo uploaded! Adjust your crop.");
      if (variantId) {
        setLogoCropOpen(true);
      }
    } catch { toast.error("Logo upload failed"); }
  };

  const handleLogoCropSave = async (cropData) => {
    const variantId = editVariant?.id;
    if (!variantId) { toast.error("Save the variant first to crop the logo"); return; }
    setLogoCropSaving(true);
    try {
      const res = await api.post("/profile/crop-qr-logo", { ...cropData, variant_id: variantId });
      updateForm((p) => ({ ...p, logo_url: res.data.url, logo_crop: res.data.crop }));
      setLogoCropOpen(false);
      setQrImages((prev) => { const n = { ...prev }; delete n[variantId]; return n; });
      toast.success("QR logo adjusted!");
    } catch {
      toast.error("Failed to save logo crop");
    } finally { setLogoCropSaving(false); }
  };

  if (authLoading || loading) return <div className="flex items-center justify-center h-64" data-testid="auth-loading-shell"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!profile) return (
    <div className="text-center py-16">
      <QrCode className="mx-auto text-muted-foreground mb-4" size={48} />
      <h2 className="text-xl font-bold mb-2">Create a Profile First</h2>
      <p className="text-muted-foreground">You need a profile before creating QR variants.</p>
    </div>
  );

  const renderVariantForm = (onSubmit, submitLabel) => {
    const selectedStyle = qrStyles.find(s => s.id === formData.qr_preset);
    const isEngraving = formData.qr_preset === 'engraving' || formData.engraving_safe;
    const presetColors = selectedStyle?.colors || { front: '#000000', back: '#ffffff' };
    const currentFront = formData.custom_colors?.front || presetColors.front;
    const currentBack = formData.custom_colors?.back || presetColors.back;
    const styleOptions = canCreative ? qrStyles : qrStyles.filter((s) => s.id === "classic");

    return (
      <div className="flex gap-5">
        {/* Left: Form */}
        <div className="flex-1 space-y-4 min-w-0 overflow-y-auto max-h-[70vh] pr-2">
          <div>
            <Label>Variant Name</Label>
            <Input value={formData.name} onChange={(e) => updateFormText((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Business Card" className="mt-1" data-testid="variant-name-input" />
          </div>

          {/* QR Style Presets */}
          <div>
            <Label className="flex items-center gap-1.5 mb-2"><Palette size={13} className="text-primary" /> Style Preset {!canCreative && <Badge variant="outline" className="text-[9px] py-0">Classic on Free</Badge>}</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="qr-style-grid">
              {styleOptions.map((s) => (
                <QrStyleSwatch key={s.id} style={s} selected={formData.qr_preset === s.id}
                  onClick={() => updateForm((p) => ({
                    ...p, qr_preset: s.id,
                    engraving_safe: s.id === 'engraving' ? true : p.engraving_safe,
                    custom_colors: null, dot_style: null, corner_style: 'square',
                  }))} />
              ))}
            </div>
            {!canCreative && (
              <p className="text-[11px] text-muted-foreground mt-1.5">Custom styles, logo, and engraving require an upgrade.</p>
            )}
          </div>

          {/* Custom Colors */}
          {canCreative && !isEngraving && (
            <div>
              <Label className="mb-2 block text-xs">Custom Colors <span className="text-muted-foreground">(override preset)</span></Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="color" value={currentFront} data-testid="color-front"
                    onChange={(e) => updateForm((p) => ({ ...p, custom_colors: { ...(p.custom_colors || {}), front: e.target.value, back: p.custom_colors?.back || currentBack } }))}
                    className="w-8 h-8 rounded border border-border cursor-pointer" />
                  <span className="text-xs">Foreground</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="color" value={currentBack} data-testid="color-back"
                    onChange={(e) => updateForm((p) => ({ ...p, custom_colors: { ...(p.custom_colors || {}), back: e.target.value, front: p.custom_colors?.front || currentFront } }))}
                    className="w-8 h-8 rounded border border-border cursor-pointer" />
                  <span className="text-xs">Background</span>
                </label>
                {formData.custom_colors && (
                  <button onClick={() => updateForm((p) => ({ ...p, custom_colors: null }))} className="text-xs text-muted-foreground hover:text-foreground underline">Reset</button>
                )}
              </div>
            </div>
          )}

          {/* Dot + Corner Styles */}
          {canCreative && !isEngraving && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1.5 block">Dot Style</Label>
                <div className="flex flex-wrap gap-1">
                  {DOT_STYLES.map((d) => {
                    const Icon = d.icon;
                    const active = (formData.dot_style || selectedStyle?.module || 'square') === d.id;
                    return (
                      <button key={d.id} onClick={() => updateForm((p) => ({ ...p, dot_style: d.id }))} data-testid={`dot-style-${d.id}`}
                        className={`flex flex-col items-center gap-0.5 p-1.5 rounded-lg border text-[9px] min-w-[48px] transition-all ${active ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/30'}`}>
                        <Icon size={14} />
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Corner Style</Label>
                <div className="flex flex-wrap gap-1">
                  {CORNER_STYLES.map((c) => (
                    <button key={c.id} onClick={() => updateForm((p) => ({ ...p, corner_style: c.id }))} data-testid={`corner-style-${c.id}`}
                      className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border text-[10px] font-medium transition-all ${formData.corner_style === c.id ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/30'}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Logo Upload */}
          {canCreative && !isEngraving && (
            <div>
              <Label className="flex items-center gap-1.5 mb-1.5 text-xs"><Upload size={12} className="text-primary" /> Center Logo <span className="text-muted-foreground">(optional)</span></Label>
              <div className="flex items-center gap-3">
                {formData.logo_url ? (
                  <div className="relative">
                    <img src={`${BACKEND_URL}${formData.logo_url}`} alt="Logo" className="w-10 h-10 rounded border object-contain bg-white" data-testid="logo-preview" />
                    <button onClick={() => updateForm((p) => ({ ...p, logo_url: null, logo_url_original: null, logo_crop: null, logo_scale: null, logo_offset_x: null, logo_offset_y: null }))} className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white rounded-full text-[9px] flex items-center justify-center">x</button>
                  </div>
                ) : null}
                {formData.logo_url && editVariant && (
                  <button onClick={() => setLogoCropOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:border-primary/40 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="adjust-logo-btn">
                    <Crop size={12} /> Adjust
                  </button>
                )}
                <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-border hover:border-primary/40 cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <Upload size={12} /> {formData.logo_url ? 'Replace' : 'Upload Logo'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
                </label>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Small brand logo centered on the QR. Auto-disabled in engraving mode.</p>
            </div>
          )}

          {/* Logo Adjustment (size + position) */}
          {canCreative && !isEngraving && formData.logo_url && (
            <div className="space-y-3 p-3 rounded-xl border border-border/50 bg-secondary/20" data-testid="logo-adjustment-panel">
              <p className="text-xs font-medium flex items-center gap-1.5"><Crosshair size={12} className="text-primary" /> Logo Adjustment</p>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px]">Size</Label>
                  <span className="text-[10px] text-muted-foreground font-mono">{Math.round((formData.logo_scale || 0.18) * 100)}%</span>
                </div>
                <input type="range" min="0.08" max="0.30" step="0.01"
                  value={formData.logo_scale || 0.18}
                  onChange={(e) => updateForm((p) => ({ ...p, logo_scale: parseFloat(e.target.value) }))}
                  className="w-full h-1.5 bg-secondary rounded-full accent-primary cursor-pointer"
                  data-testid="logo-scale-slider" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px]">Horizontal</Label>
                  <span className="text-[10px] text-muted-foreground font-mono">{formData.logo_offset_x ? (formData.logo_offset_x > 0 ? 'R' : 'L') + Math.abs(Math.round(formData.logo_offset_x * 100)) : '0'}</span>
                </div>
                <input type="range" min="-0.35" max="0.35" step="0.01"
                  value={formData.logo_offset_x || 0}
                  onChange={(e) => updateForm((p) => ({ ...p, logo_offset_x: parseFloat(e.target.value) }))}
                  className="w-full h-1.5 bg-secondary rounded-full accent-primary cursor-pointer"
                  data-testid="logo-offset-x-slider" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px]">Vertical</Label>
                  <span className="text-[10px] text-muted-foreground font-mono">{formData.logo_offset_y ? (formData.logo_offset_y > 0 ? 'D' : 'U') + Math.abs(Math.round(formData.logo_offset_y * 100)) : '0'}</span>
                </div>
                <input type="range" min="-0.35" max="0.35" step="0.01"
                  value={formData.logo_offset_y || 0}
                  onChange={(e) => updateForm((p) => ({ ...p, logo_offset_y: parseFloat(e.target.value) }))}
                  className="w-full h-1.5 bg-secondary rounded-full accent-primary cursor-pointer"
                  data-testid="logo-offset-y-slider" />
              </div>
              <button onClick={() => updateForm((p) => ({ ...p, logo_scale: 0.18, logo_offset_x: 0, logo_offset_y: 0 }))}
                className="text-[10px] text-muted-foreground hover:text-primary transition-colors"
                data-testid="logo-reset-btn">
                Reset to center
              </button>
            </div>
          )}

          {/* Frame Text */}
          {canCreative && (
          <div>
            <Label className="flex items-center gap-1.5 text-xs"><Type size={12} className="text-primary" /> Frame Caption</Label>
            <Input value={formData.frame_text || ''} 
              onChange={(e) => {
                const newValue = e.target.value;
                setFormData(prev => {
                  const updated = { ...prev, frame_text: newValue };
                  // Trigger preview with updated data
                  clearTimeout(previewTimer.current);
                  previewTimer.current = setTimeout(() => fetchPreview(updated), 400);
                  return updated;
                });
              }}
              placeholder='e.g. SCAN ME' className="mt-1 h-8 text-xs" data-testid="frame-text-input" />
            <p className="text-[10px] text-muted-foreground mt-1">Text appears below the QR code</p>
          </div>
          )}

          {/* Engraving Toggle */}
          {canCreative && (
          <div className={`flex items-center justify-between py-2.5 px-3 rounded-xl border transition-colors ${isEngraving ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-secondary/30'}`}>
            <div className="flex items-center gap-2">
              <Crosshair size={14} className={isEngraving ? 'text-amber-500' : 'text-muted-foreground'} />
              <div>
                <p className="text-xs font-medium">Laser Engraving Mode</p>
                <p className="text-[10px] text-muted-foreground">High-contrast, thick modules, no logo</p>
              </div>
            </div>
            <Switch checked={formData.engraving_safe} data-testid="engraving-safe-toggle"
              onCheckedChange={(v) => updateForm((p) => ({
                ...p, engraving_safe: v,
                qr_preset: v ? 'engraving' : (p.qr_preset === 'engraving' ? 'classic' : p.qr_preset),
                custom_colors: v ? null : p.custom_colors, logo_url: v ? null : p.logo_url,
              }))} />
          </div>
          )}

          {/* Export Preferences */}
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5 text-xs"><Download size={12} className="text-primary" /> Export Preferences</Label>
            <div className="grid grid-cols-2 gap-2">
              <Select value={formData.export_preferences?.default_format || 'png'} onValueChange={(v) => updateForm((p) => ({ ...p, export_preferences: { ...p.export_preferences, default_format: v } }))}>
                <SelectTrigger className="h-7 text-xs" data-testid="export-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="png">PNG</SelectItem>
                  {canSvg && <SelectItem value="svg">SVG</SelectItem>}
                  {canPdf && <SelectItem value="pdf">PDF</SelectItem>}
                </SelectContent>
              </Select>
              <Select value={formData.export_preferences?.default_size || 'standard'} onValueChange={(v) => updateForm((p) => ({ ...p, export_preferences: { ...p.export_preferences, default_size: v } }))}>
                <SelectTrigger className="h-7 text-xs" data-testid="export-size-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="standard">Standard</SelectItem><SelectItem value="hires">Hi-Resolution</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          {/* Content Preset - Visual Cards */}
          <div>
            <Label className="text-xs mb-2 block">Content Preset</Label>
            <div className="grid grid-cols-3 gap-2" data-testid="content-preset-grid">
              {PRESETS.map((p) => {
                const PresetIcon = p.icon;
                const isActive = formData.preset === p.value;
                return (
                  <button key={p.value} onClick={() => handlePresetChange(p.value)} data-testid={`preset-card-${p.value}`}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                      isActive ? 'border-primary bg-primary/5 ring-1 ring-primary/20 shadow-sm' : 'border-border/60 hover:border-primary/30 bg-card hover:bg-secondary/40'
                    }`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? `bg-gradient-to-br ${p.accent} shadow-sm` : 'bg-secondary/60'}`}>
                      <PresetIcon size={15} className={isActive ? 'text-white' : 'text-muted-foreground'} />
                    </div>
                    <span className={`text-[10px] font-medium leading-tight ${isActive ? 'text-primary' : ''}`}>{p.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">{PRESETS.find(p => p.value === formData.preset)?.desc}</p>
          </div>

          {/* Visible Sections */}
          <div>
            <Label className="mb-1.5 block text-xs">Visible Sections</Label>
            <div className="space-y-1.5">
              {SECTION_KEYS.map((s) => (
                <div key={s.key} className="flex items-center justify-between py-1 px-2.5 rounded-lg border border-border bg-secondary/30">
                  <span className="text-xs">{s.label}</span>
                  <Switch checked={formData.visible_sections[s.key] ?? true} onCheckedChange={() => toggleSection(s.key)} data-testid={`section-toggle-${s.key}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Avatar Position (only when avatar is visible) */}
          {formData.visible_sections.show_avatar && (
            <div className="space-y-3 p-3 rounded-xl border border-border/50 bg-secondary/20" data-testid="avatar-position-panel">
              <p className="text-xs font-medium flex items-center gap-1.5"><Users size={12} className="text-primary" /> Avatar Position</p>
              <p className="text-[10px] text-muted-foreground">Move the avatar left/right and up/down on the public profile</p>
              
              <div className="space-y-2">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px]">Horizontal</Label>
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {(formData.visible_sections.avatar_offset_x || 0) === 0 ? 'Center' : 
                       (formData.visible_sections.avatar_offset_x > 0 ? `Right ${formData.visible_sections.avatar_offset_x}px` : `Left ${Math.abs(formData.visible_sections.avatar_offset_x)}px`)}
                    </span>
                  </div>
                  <input type="range" min="-80" max="80" step="5"
                    value={formData.visible_sections.avatar_offset_x || 0}
                    onChange={(e) => updateForm((p) => ({ ...p, visible_sections: { ...p.visible_sections, avatar_offset_x: parseInt(e.target.value) } }))}
                    className="w-full h-1.5 bg-secondary rounded-full accent-primary cursor-pointer"
                    data-testid="avatar-offset-x-slider" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px]">Vertical</Label>
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {(formData.visible_sections.avatar_offset_y || 0) === 0 ? 'Center' : 
                       (formData.visible_sections.avatar_offset_y > 0 ? `Down ${formData.visible_sections.avatar_offset_y}px` : `Up ${Math.abs(formData.visible_sections.avatar_offset_y)}px`)}
                    </span>
                  </div>
                  <input type="range" min="-50" max="50" step="5"
                    value={formData.visible_sections.avatar_offset_y || 0}
                    onChange={(e) => updateForm((p) => ({ ...p, visible_sections: { ...p.visible_sections, avatar_offset_y: parseInt(e.target.value) } }))}
                    className="w-full h-1.5 bg-secondary rounded-full accent-primary cursor-pointer"
                    data-testid="avatar-offset-y-slider" />
                </div>
                <button 
                  onClick={() => updateForm((p) => ({ ...p, visible_sections: { ...p.visible_sections, avatar_offset_x: 0, avatar_offset_y: 0 } }))}
                  className="text-[10px] text-muted-foreground hover:text-primary transition-colors"
                  data-testid="avatar-position-reset-btn">
                  Reset to center
                </button>
              </div>
            </div>
          )}

          {formData.visible_sections.show_links && links.length > 0 && (
            <div>
              <Label className="mb-1.5 block text-xs">Filter Links <span className="text-muted-foreground">(all unchecked = show all)</span></Label>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {links.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 py-1 px-2.5 rounded-lg border border-border bg-secondary/30 cursor-pointer text-xs">
                    <input type="checkbox" checked={formData.link_ids.includes(l.id)} onChange={() => toggleLinkId(l.id)} className="rounded" />{l.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Contact Overrides (for vCard) */}
          {(formData.preset === 'contact' || formData.preset === 'business' || formData.preset === 'full') && (
            <div>
              <Label className="flex items-center gap-1.5 text-xs mb-2"><UserPlus size={12} className="text-primary" /> vCard Contact Overrides <span className="text-muted-foreground">(optional)</span></Label>
              <p className="text-[10px] text-muted-foreground mb-3">Override profile contact info for this variant's Save Contact / vCard download. Leave empty to use profile defaults.</p>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="flex items-center gap-1 text-[10px] text-muted-foreground mb-0.5"><Phone size={9} /> Full Name</label>
                    <Input value={formData.contact_overrides?.full_name || ''} onChange={(e) => updateFormText((p) => ({ ...p, contact_overrides: { ...p.contact_overrides, full_name: e.target.value } }))} placeholder="John Doe" className="h-7 text-xs" data-testid="contact-override-name" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-[10px] text-muted-foreground mb-0.5"><Building2 size={9} /> Company</label>
                    <Input value={formData.contact_overrides?.company || ''} onChange={(e) => updateFormText((p) => ({ ...p, contact_overrides: { ...p.contact_overrides, company: e.target.value } }))} placeholder="Acme Corp" className="h-7 text-xs" data-testid="contact-override-company" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="flex items-center gap-1 text-[10px] text-muted-foreground mb-0.5"><Phone size={9} /> Phone</label>
                    <Input value={formData.contact_overrides?.phone || ''} onChange={(e) => updateFormText((p) => ({ ...p, contact_overrides: { ...p.contact_overrides, phone: e.target.value } }))} placeholder="+1-555-0123" className="h-7 text-xs" data-testid="contact-override-phone" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-[10px] text-muted-foreground mb-0.5"><Mail size={9} /> Email</label>
                    <Input value={formData.contact_overrides?.email || ''} onChange={(e) => updateFormText((p) => ({ ...p, contact_overrides: { ...p.contact_overrides, email: e.target.value } }))} placeholder="contact@example.com" className="h-7 text-xs" data-testid="contact-override-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="flex items-center gap-1 text-[10px] text-muted-foreground mb-0.5"><Globe size={9} /> Website</label>
                    <Input value={formData.contact_overrides?.website || ''} onChange={(e) => updateFormText((p) => ({ ...p, contact_overrides: { ...p.contact_overrides, website: e.target.value } }))} placeholder="https://..." className="h-7 text-xs" data-testid="contact-override-website" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-[10px] text-muted-foreground mb-0.5"><MapPin size={9} /> Address</label>
                    <Input value={formData.contact_overrides?.address || ''} onChange={(e) => updateFormText((p) => ({ ...p, contact_overrides: { ...p.contact_overrides, address: e.target.value } }))} placeholder="123 Main St" className="h-7 text-xs" data-testid="contact-override-address" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Animation */}
          <div className="space-y-2 p-3 rounded-xl border border-border/50 bg-secondary/20">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Animation Effect</Label>
              <Switch checked={formData.animation} data-testid="variant-animation-toggle"
                onCheckedChange={(v) => updateForm((p) => ({ ...p, animation: v, animation_type: v ? (p.animation_type === 'none' ? 'bounce-in' : p.animation_type) : 'none' }))} />
            </div>
            {formData.animation && (
              <div className="space-y-2">
                <Select value={formData.animation_type} onValueChange={(v) => updateForm((p) => ({ ...p, animation_type: v }))}>
                  <SelectTrigger className="h-9 text-xs" data-testid="variant-animation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ANIMATION_TYPES.filter((a) => a.value !== 'none').map((a) => (
                      <SelectItem key={a.value} value={a.value}>
                        <div className="flex flex-col py-0.5">
                          <span className="font-medium">{a.label}</span>
                          <span className="text-[10px] text-muted-foreground">{a.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Elements will animate in sequence when the profile loads. More dramatic animations create a memorable first impression!
                </p>
              </div>
            )}
          </div>

          {/* Audio */}
          <div>
            <Label className="text-xs">Background Audio <span className="text-muted-foreground">(optional)</span></Label>
            <Input value={formData.audio_url} onChange={(e) => updateFormText((p) => ({ ...p, audio_url: e.target.value }))} placeholder="https://example.com/audio.mp3" className="mt-1 h-8 text-xs" data-testid="variant-audio-input" />
          </div>

          <Button onClick={onSubmit} className="w-full btn-glow" data-testid="variant-form-submit">{submitLabel}</Button>
        </div>

        {/* Right: Live Preview */}
        <div className="hidden sm:flex flex-col items-center justify-start pt-2 shrink-0 sticky top-4">
          <LivePreview
            styleData={formData}
            loading={previewLoading}
            previewImage={previewImage}
            hasLogo={!!(formData.logo_url && !formData.engraving_safe)}
            onLogoOffsetChange={(ox, oy) => {
              updateForm((p) => ({ ...p, logo_offset_x: Math.round(ox * 100) / 100, logo_offset_y: Math.round(oy * 100) / 100 }));
            }}
          />
          {formData.engraving_safe && (
            <Badge className="mt-2 bg-amber-500/10 text-amber-600 border-amber-500/30 text-[9px]"><Crosshair size={10} className="mr-1" /> Engraving Safe</Badge>
          )}
          <div className="mt-3 text-center">
            <p className="text-[10px] text-muted-foreground">
              {PRESETS.find(p => p.value === formData.preset)?.label || 'Full Profile'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const getStyleName = (v) => {
    const sid = (v.style || {}).qr_preset || 'classic';
    return qrStyles.find(s => s.id === sid)?.name || 'Classic Clean';
  };

  return (
    <div data-testid="qr-variants-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold">QR Variants</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {maxVariants == null
              ? "Loading plan limits…"
              : maxVariants < 0
              ? `Unlimited QR designs on your plan (${variants.length} created)`
              : `Create up to ${maxVariants} QR code${maxVariants === 1 ? "" : "s"} on your plan (${variants.length}/${maxVariants})`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {variants.length > 0 && canSvg && (
            <Button variant="outline" className="gap-2" onClick={handleDownloadPack} disabled={packDownloading} data-testid="download-pack-btn">
              <Package size={16} /> {packDownloading ? "Zipping..." : "Download All"}
            </Button>
          )}
          <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-glow gap-2" disabled={!canCreateMore} data-testid="create-variant-btn"><Plus size={16} /> New Variant</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create QR Variant</DialogTitle></DialogHeader>
              {renderVariantForm(handleCreate, "Create Variant")}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="wallet-help-banner">
        <WalletCards size={19} className="text-primary shrink-0" />
        <p className="text-xs text-muted-foreground flex-1">
          <span className="font-semibold text-foreground">{t("wallet.devices")}</span>{' '}
          {t("wallet.devicesText")}
        </p>
        <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2.5 self-start sm:self-auto" onClick={() => { window.location.href = '/dashboard/help'; }}>
          <CircleHelp size={14} /> {t("wallet.guide")}
        </Button>
      </div>

      {variants.length === 0 ? (
        <Card className="card-hover border-dashed">
          <CardContent className="py-16 text-center">
            <QrCode className="mx-auto text-muted-foreground mb-4" size={48} />
            <h3 className="font-heading text-lg font-semibold mb-2">No QR Variants Yet</h3>
            <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">Create different QR codes that show different views of your profile.</p>
            <Button onClick={() => setCreateOpen(true)} className="btn-glow gap-2" data-testid="create-first-variant-btn"><Plus size={16} /> Create Your First Variant</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {variants.map((v) => {
            const vStyle = v.style || {};
            const isEng = vStyle.qr_preset === 'engraving' || vStyle.engraving_safe;
            const scanCount = analyticsData?.total_scans;
            return (
              <Card key={v.id} className="card-hover overflow-hidden" data-testid={`variant-card-${v.id}`}>
                <div className="flex">
                  {/* QR Preview Column */}
                  <div className="shrink-0 p-4 flex flex-col items-center justify-center bg-white dark:bg-zinc-100 border-r border-border/30">
                    {variantPreviews[v.id] ? (
                      <img src={variantPreviews[v.id]} alt={v.name} className="w-28 h-28 object-contain" data-testid={`qr-image-${v.id}`} />
                    ) : (
                      <div className="w-28 h-28 rounded-md animate-pulse bg-gray-100" />
                    )}
                  </div>

                  {/* Content Column */}
                  <div className="flex-1 min-w-0 p-4">
                    {/* Header row */}
                    <div className="flex items-start justify-between mb-2.5">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm truncate" data-testid={`variant-name-${v.id}`}>{v.name}</h3>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <Badge variant="secondary" className="text-[10px] py-0">{PRESETS.find((p) => p.value === v.preset)?.label || v.preset}</Badge>
                          <Badge variant="outline" className="text-[10px] py-0 gap-0.5"><Palette size={8} /> {getStyleName(v)}</Badge>
                          {isEng && <Badge className="text-[10px] py-0 bg-amber-500/10 text-amber-600 border-amber-500/30"><Crosshair size={8} /></Badge>}
                          {vStyle.logo_url && <Badge variant="outline" className="text-[10px] py-0">Logo</Badge>}
                          {v.animation && <Badge variant="outline" className="text-[10px] py-0">Animated</Badge>}
                          {(v.contact_overrides && Object.values(v.contact_overrides).some(val => val)) && <Badge variant="outline" className="text-[10px] py-0 gap-0.5"><UserPlus size={8} /> vCard</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-0.5 shrink-0">
                        <button onClick={() => openEdit(v)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" data-testid={`edit-variant-${v.id}`}><Pencil size={13} /></button>
                        <button onClick={() => handleDelete(v.id)} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-variant-${v.id}`}><Trash2 size={13} /></button>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <Button variant="outline" size="sm" className="gap-1 text-[11px] h-7 px-2.5" onClick={() => copyShareLink(v)} data-testid={`share-link-${v.id}`}><Share2 size={11} /> Share</Button>
                      <Button variant="outline" size="sm" className="gap-1 text-[11px] h-7 px-2.5" onClick={() => setExportVariant(v)} data-testid={`export-variant-${v.id}`}><Download size={11} /> Export</Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-[11px] h-7 px-2.5"
                        onClick={() => handleAddToWallet(v)}
                        disabled={walletDownloading === v.id}
                        title={canWallet ? t("wallet.title") : t("wallet.available")}
                        data-testid={`apple-wallet-${v.id}`}
                      >
                        {canWallet ? <WalletCards size={11} /> : <Lock size={11} />}
                        {walletDownloading === v.id ? t("wallet.creating") : canWallet ? t("wallet.button") : t("wallet.pro")}
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1 text-[11px] h-7 px-2.5" onClick={() => window.open(`/p/${profile.username}?v=${v.id}`, '_blank')} data-testid={`preview-variant-${v.id}`}><Eye size={11} /> Preview</Button>
                      <Button variant="outline" size="sm" className="gap-1 text-[11px] h-7 px-2.5" onClick={() => openAnalytics(v)} data-testid={`analytics-variant-${v.id}`}><BarChart3 size={11} /> Stats</Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editVariant} onOpenChange={(open) => { if (!open) { setEditVariant(null); resetForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Variant</DialogTitle></DialogHeader>
          {renderVariantForm(handleUpdate, "Save Changes")}
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={!!analyticsVariant} onOpenChange={(open) => { if (!open) { setAnalyticsVariant(null); setAnalyticsData(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 size={18} /> {analyticsVariant?.name} — Analytics</DialogTitle>
          </DialogHeader>
          {analyticsData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-secondary/50 text-center">
                  <p className="text-2xl font-bold">{analyticsData.total_scans}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total Scans</p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/50 text-center">
                  <p className="text-2xl font-bold">{analyticsData.daily_scans?.length || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Active Days</p>
                </div>
              </div>
              {Object.keys(analyticsData.device_stats || {}).length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Devices</p>
                  <div className="space-y-1.5">
                    {Object.entries(analyticsData.device_stats).map(([device, count]) => (
                      <div key={device} className="flex items-center justify-between text-sm px-3 py-1.5 rounded bg-secondary/30"><span className="capitalize">{device}</span><span className="font-medium">{count}</span></div>
                    ))}
                  </div>
                </div>
              )}
              {analyticsData.total_scans === 0 && <p className="text-center text-sm text-muted-foreground py-4">No scans yet for this variant.</p>}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Logo Crop Editor */}
      {formData.logo_url && (formData.logo_url_original || formData.logo_url) && (
        <ImageCropEditor
          open={logoCropOpen}
          onClose={() => setLogoCropOpen(false)}
          imageUrl={`${BACKEND_URL}${formData.logo_url_original || formData.logo_url}`}
          initialCrop={formData.logo_crop}
          onSave={handleLogoCropSave}
          saving={logoCropSaving}
          title="Adjust QR Center Logo"
          aspect={1}
          cropShape="rect"
          containerClass="aspect-square"
          hint="Drag to reposition. Use the slider or pinch to zoom. Logo stays safely sized for QR readability."
        />
      )}

      {/* Export Modal */}
      {exportVariant && (
        <QrExportModal
          open={!!exportVariant}
          onClose={() => setExportVariant(null)}
          variant={exportVariant}
        />
      )}

      {/* Success State After Creation */}
      <Dialog open={!!successVariant} onOpenChange={(open) => { if (!open) setSuccessVariant(null); }}>
        <DialogContent className="max-w-sm" data-testid="variant-success-dialog">
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-5">
              <CheckCircle2 size={36} className="text-emerald-500" />
            </div>
            <h3 className="font-heading text-lg font-bold mb-1">Variant Created!</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Your QR variant "{successVariant?.name}" is ready to share.
            </p>
            <div className="w-full space-y-2">
              <Button className="w-full btn-glow gap-2" onClick={() => { setExportVariant(successVariant); setSuccessVariant(null); }} data-testid="success-export-btn">
                <Download size={16} /> Export QR Code
              </Button>
              <Button variant="outline" className="w-full gap-2" onClick={() => {
                if (successVariant?.token) {
                  navigator.clipboard.writeText(`${window.location.origin}/v/${successVariant.token}`);
                  toast.success("Share link copied!");
                }
              }} data-testid="success-share-btn">
                <Share2 size={16} /> Copy Share Link
              </Button>
              {profile && successVariant && (
                <Button variant="ghost" className="w-full gap-2 text-muted-foreground" onClick={() => {
                  window.open(`/p/${profile.username}?v=${successVariant.id}`, '_blank');
                }} data-testid="success-preview-btn">
                  <Eye size={16} /> Preview Live Page
                </Button>
              )}
              <Button variant="ghost" className="w-full text-xs text-muted-foreground" onClick={() => setSuccessVariant(null)} data-testid="success-close-btn">
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
